import React from "react";

import { differenceInHours } from "date-fns";
import { Box, Grid, Paper, Typography } from "@mui/material";

import { ClusterStatus } from "../types/cluster";
import Sparkline from "./sparkline";

interface StatTilesProps {
  clusters: ClusterStatus[];
}

const formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const eyebrow = {
  fontFamily: "'IBM Plex Mono', ui-monospace, Menlo, monospace",
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: "0.12em",
  textTransform: "uppercase" as const,
  color: "text.secondary",
};

const valueSx = (tone: "ok" | "warn" | "err" | "neutral") => ({
  fontFamily: "'IBM Plex Serif', ui-serif, Georgia, serif",
  fontSize: 32,
  fontWeight: 500,
  letterSpacing: "-0.02em",
  lineHeight: 1.1,
  color:
    tone === "ok"
      ? "var(--ok)"
      : tone === "warn"
      ? "var(--warn)"
      : tone === "err"
      ? "var(--err)"
      : "text.primary",
});

const tileSx = {
  p: 2.5,
  borderRadius: 1,
  border: "1px solid",
  borderColor: "divider",
  backgroundColor: "background.paper",
  boxShadow: "var(--shadow-1)",
  display: "flex",
  flexDirection: "column",
  gap: 1.25,
  height: "100%",
};

// Deterministic pseudo-random sparkline seeded off a string. Used to stand in
// for real time-series data until a metrics endpoint exists.
const seededSeries = (seed: string, count: number, amp = 1): number[] => {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) {
    h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  }
  const out: number[] = [];
  for (let i = 0; i < count; i += 1) {
    h = (h * 1103515245 + 12345) & 0x7fffffff;
    out.push(((h % 1000) / 1000) * amp);
  }
  return out;
};

const StatTiles: React.FC<StatTilesProps> = ({ clusters }) => {
  const running = clusters.filter((c) => c.micro?.stage === "done").length;
  const creating = clusters.filter(
    (c) => c.micro?.stage && c.micro.stage !== "done" && !(c.micro.failures || []).length,
  ).length;
  const failing = clusters.filter((c) => (c.micro?.failures || []).length > 0).length;

  const currentPennies = clusters.reduce((acc, c) => acc + (c.micro?.usage || 0), 0);

  // Projected monthly burn: extrapolate each cluster's hourly burn to 30×24h.
  const projectedPennies = clusters.reduce((acc, c) => {
    const hours = Math.max(
      differenceInHours(new Date(), new Date(c.created)) || 0,
      1,
    );
    const perHour = (c.micro?.usage || 0) / hours;
    return acc + perHour * 24 * 30;
  }, 0);

  const fleetTone: "ok" | "warn" | "err" | "neutral" =
    failing > 0 ? "err" : creating > 0 ? "warn" : running > 0 ? "ok" : "neutral";

  // Cost sparkline — seeded off cluster names so it's stable across renders
  // but varied between workspaces. Swap for real metrics once available.
  const costSeed = clusters.map((c) => c.name).join("|") || "empty";
  const costSeries = seededSeries(costSeed, 18, 1);
  // Make the projected series trend upward through the seeded noise
  const projectedSeries = costSeries.map((v, i, arr) =>
    v * 0.5 + (i / arr.length) * 0.6,
  );

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={6} md={3}>
        <Paper elevation={0} sx={tileSx}>
          <Typography sx={eyebrow}>Clusters</Typography>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
            <Typography sx={valueSx("neutral")}>{clusters.length}</Typography>
            <Typography
              sx={{
                fontFamily: "'IBM Plex Mono', ui-monospace, Menlo, monospace",
                fontSize: 13,
                color: "text.secondary",
              }}
            >
              {clusters.length === 1 ? "cluster" : "clusters"}
            </Typography>
          </Box>
          <Sparkline
            values={seededSeries(costSeed + ":count", 18, 1).map(
              (v) => v * 0.4 + clusters.length,
            )}
            stroke="var(--fg-3)"
          />
        </Paper>
      </Grid>

      <Grid item xs={6} md={3}>
        <Paper elevation={0} sx={tileSx}>
          <Typography sx={eyebrow}>Healthy</Typography>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
            <Typography sx={valueSx(fleetTone)}>{running}</Typography>
            <Typography
              sx={{
                fontFamily: "'IBM Plex Mono', ui-monospace, Menlo, monospace",
                fontSize: 13,
                color: "text.secondary",
              }}
            >
              / {clusters.length || 0}
            </Typography>
          </Box>
          <FleetBar running={running} creating={creating} failing={failing} />
        </Paper>
      </Grid>

      <Grid item xs={6} md={3}>
        <Paper elevation={0} sx={tileSx}>
          <Typography sx={eyebrow}>Month-to-date</Typography>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
            <Typography sx={valueSx("neutral")}>
              {formatter.format(currentPennies / 100)}
            </Typography>
          </Box>
          <Sparkline
            values={costSeries}
            stroke="var(--accent)"
            fill="var(--accent-soft)"
          />
        </Paper>
      </Grid>

      <Grid item xs={6} md={3}>
        <Paper elevation={0} sx={tileSx}>
          <Typography sx={eyebrow}>Projected monthly</Typography>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
            <Typography sx={valueSx("neutral")}>
              {formatter.format(projectedPennies / 100)}
            </Typography>
            <Typography
              sx={{
                fontFamily: "'IBM Plex Mono', ui-monospace, Menlo, monospace",
                fontSize: 13,
                color: "text.secondary",
              }}
            >
              at current burn
            </Typography>
          </Box>
          <Sparkline
            values={projectedSeries}
            stroke="var(--warn)"
            fill="rgba(219, 144, 35, 0.12)"
          />
        </Paper>
      </Grid>
    </Grid>
  );
};

// Small stacked bar showing fleet health composition. Segments in node green,
// sky blue (creating/updating), rust red (failing). Background is paper.
const FleetBar: React.FC<{
  running: number;
  creating: number;
  failing: number;
}> = ({ running, creating, failing }) => {
  const total = running + creating + failing;
  if (total === 0) {
    return (
      <Box
        sx={{
          height: 6,
          borderRadius: 999,
          backgroundColor: "var(--border-2)",
        }}
      />
    );
  }
  const toPct = (n: number) => ((n / total) * 100).toFixed(2);
  return (
    <Box sx={{ display: "flex", gap: 0.5, height: 6 }}>
      {running > 0 && (
        <Box
          sx={{
            flex: `0 0 ${toPct(running)}%`,
            borderRadius: 999,
            backgroundColor: "var(--ok)",
          }}
          title={`${running} running`}
        />
      )}
      {creating > 0 && (
        <Box
          sx={{
            flex: `0 0 ${toPct(creating)}%`,
            borderRadius: 999,
            backgroundColor: "var(--info)",
          }}
          title={`${creating} creating`}
        />
      )}
      {failing > 0 && (
        <Box
          sx={{
            flex: `0 0 ${toPct(failing)}%`,
            borderRadius: 999,
            backgroundColor: "var(--err)",
          }}
          title={`${failing} failing`}
        />
      )}
    </Box>
  );
};

export default StatTiles;
